import React, { useState, useEffect } from "react";
import { Search, Upload, FileText, Image, File, Download, Eye } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { TextInput } from "@mantine/core";
import { Endpoints } from "../../../lib/endpoints";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { TableLoader } from "../../../components/Loader/LoadingOverlay";

interface Document {
  id: number | string;
  name: string;
  type: string;
  status: string;
  uploaded: string;
  size: string;
  file_url?: string;
}

interface DocumentsResponse {
  results: Document[];
  count: number;
}

export default function Documents() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [documents, setDocuments] = useState<Document[]>([]);

  useEffect(() => {
    fetchDocuments();
  }, [searchQuery]);

  async function fetchDocuments() {
    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetDocuments({
        search: searchQuery || undefined,
      });
      const response = await PerformRequest<DocumentsResponse>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const data = Array.isArray(response.data)
          ? { results: response.data, count: response.data.length }
          : response.data;
        setDocuments(data.results || []);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to load documents. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to load documents. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleUpload = async (file: File) => {
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", file.name);

      const endpoint = Endpoints.UploadDocument();
      const response = await PerformRequest<Document>({
        method: endpoint.method,
        route: endpoint.route,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 201 || response.status === 200) {
        notifications.show({
          title: "Success",
          message: "Document uploaded successfully!",
          color: "green",
        });
        fetchDocuments();
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to upload document. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to upload document. Please try again.",
        color: "red",
      });
    }
  };

  const handleDownload = async (documentId: string | number) => {
    try {
      const endpoint = Endpoints.DownloadDocument(String(documentId));
      const response = await PerformRequest<Blob>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = `document-${documentId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to download document. Please try again.",
        color: "red",
      });
    }
  };

  const mockItems = [
    {
      id: 1,
      name: "Deed of Assignment",
      type: "PDF",
      status: "Verified",
      uploaded: "Dec 11, 2024",
      size: "2.4 MB",
    },
    {
      id: 2,
      name: "Survey Plan",
      type: "Image",
      status: "Pending",
      uploaded: "Dec 09, 2024",
      size: "1.8 MB",
    },
    {
      id: 3,
      name: "Certificate of Occupancy",
      type: "PDF",
      status: "Verified",
      uploaded: "Dec 05, 2024",
      size: "3.1 MB",
    },
    {
      id: 4,
      name: "Title Deed",
      type: "PDF",
      status: "Verified",
      uploaded: "Dec 03, 2024",
      size: "1.5 MB",
    },
    {
      id: 5,
      name: "Site Plan",
      type: "Image",
      status: "Pending",
      uploaded: "Nov 28, 2024",
      size: "2.2 MB",
    },
  ];

  const getFileIcon = (type: string) => {
    if (type === "PDF") return <FileText size={16} />;
    if (type === "Image") return <Image size={16} />;
    return <File size={16} />;
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <div className="dashboard-page__header-left">
          <h1 className="dashboard-page__title">Documents</h1>
          <p className="dashboard-page__subtitle">
            Upload, verify, and manage transaction documents.
          </p>
        </div>
      </div>

      <div className="dashboard-filters">
        <TextInput
          placeholder="Search documents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          leftSection={<Search size={18} />}
          style={{ flex: 1 }}
        />
        <label className="dashboard-button" style={{ cursor: "pointer" }}>
          <Upload size={16} />
          <span>Upload Document</span>
          <input
            type="file"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleUpload(file);
              }
            }}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
        </label>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">All Documents</h2>
        </div>
        <div className="dashboard-table-wrap">
          <table className="dashboard-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Uploaded</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableLoader colSpan={6} />
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    No documents found
                  </td>
                </tr>
              ) : (
                documents.map((d) => (
                  <tr
                    key={d.id}
                    className="transition-colors duration-200 hover:bg-black/5"
                  >
                    <td>
                      <div className="dashboard-table__file">
                        <span className="dashboard-table__file-icon">
                          {getFileIcon(d.type)}
                        </span>
                        <span>{d.name}</span>
                      </div>
                    </td>
                    <td>
                      <span className="dashboard-table__type">{d.type}</span>
                    </td>
                    <td>{d.size}</td>
                    <td>
                      <span
                        className={
                          d.status === "Verified"
                            ? "badge badge--success"
                            : "badge badge--pending"
                        }
                      >
                        {d.status}
                      </span>
                    </td>
                    <td>{d.uploaded}</td>
                    <td>
                      <div className="dashboard-table__actions">
                        {d.file_url && (
                          <a
                            href={d.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="dashboard-table__action"
                            aria-label="View"
                          >
                            <Eye size={14} />
                          </a>
                        )}
                        <button
                          className="dashboard-table__action"
                          type="button"
                          onClick={() => handleDownload(d.id)}
                          aria-label="Download"
                        >
                          <Download size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
